import React from 'react'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import LogoutIcon from '@mui/icons-material/Logout'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { ICON_CELL, ICON_INSET, SIDEBAR_TRANSITION_MS, fade, reducedMotion } from './sidebarMotion'

interface SidebarUserBlockProps {
  userName: string
  isCollapsed: boolean
  isDarkMode: boolean
  onLogoutClick: () => void
}

const FONT =
  'Rubik, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif'

const SidebarUserBlock: React.FC<SidebarUserBlockProps> = ({
  userName,
  isCollapsed,
  isDarkMode,
  onLogoutClick,
}) => {
  const red = isDarkMode ? '#FF6B6B' : '#DC2626'

  const logoutButton = (
    <Tooltip title="התנתקות" placement="left" arrow>
      <IconButton
        aria-label="התנתקות"
        tabIndex={isCollapsed ? -1 : 0}
        onClick={onLogoutClick}
        sx={{
          width: 28,
          flexShrink: 0,
          height: 28,
          borderRadius: '6px',
          border: `1px solid ${red}`,
          color: red,
          '&:hover': {
            backgroundColor: isDarkMode
              ? 'rgba(239, 68, 68, 0.14)'
              : 'rgba(220, 38, 38, 0.08)',
          },
        }}
      >
        <LogoutIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  )

  const userIcon = (
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        backgroundColor: isDarkMode ? 'rgba(99, 179, 237, 0.18)' : '#DDEFFC',
        color: isDarkMode ? '#FFFFFF' : '#003F61',
      }}
    >
      <PersonOutlineIcon sx={{ fontSize: 20 }} />
    </Box>
  )

  return (
    <Box
      data-testid="sidebar-user"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        boxSizing: 'border-box',
        width: '100%',
        overflow: 'hidden',
        pl: 1.5,
        pr: ICON_INSET,
        py: 1.5,
        direction: 'ltr',
      }}
    >
      <Box
        data-testid="sidebar-user-text"
        aria-hidden={isCollapsed}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flex: '1 1 0',
          minWidth: 0,
          overflow: 'hidden',
          // A closed tray leaves this group under 1px wide; hide it once the fade ends so no sliver shows.
          visibility: isCollapsed ? 'hidden' : 'visible',
          transition: `visibility 0s linear ${isCollapsed ? SIDEBAR_TRANSITION_MS : 0}ms`,
          ...reducedMotion,
        }}
      >
        <Box sx={{ flexShrink: 0, display: 'flex', ...fade(!isCollapsed) }}>
          {logoutButton}
        </Box>
        <Box
          sx={{
            flex: '1 1 0',
            minWidth: 0,
            overflow: 'hidden',
            textAlign: 'right',
            ...fade(!isCollapsed),
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: 10,
              whiteSpace: 'nowrap',
              color: isDarkMode ? '#94A3B8' : '#979EB0',
              lineHeight: 1.3,
            }}
          >
            ! שלום
          </Typography>
          <Typography
            noWrap
            sx={{
              fontFamily: FONT,
              fontSize: 14,
              fontWeight: 600,
              lineHeight: 1.3,
              color: isDarkMode ? '#FFFFFF' : '#003F61',
            }}
          >
            {userName}
          </Typography>
        </Box>
      </Box>
      <Box
        data-testid="sidebar-user-avatar"
        sx={{ width: ICON_CELL, flexShrink: 0, display: 'flex', justifyContent: 'center' }}
      >
        {userIcon}
      </Box>
    </Box>
  )
}

export default SidebarUserBlock
